/* eslint-disable style/quote-props */
import type { StepIconProps } from '@mui/material/StepIcon';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MobileStepper from '@mui/material/MobileStepper';
import Step from '@mui/material/Step';
import StepConnector, {
  stepConnectorClasses,
} from '@mui/material/StepConnector';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import { styled as MuiStyled, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import kandirLogoLink from '../../../assets/images/kandir_logo.png';
import kandirLogoGreyLink from '../../../assets/images/kandir_logo_grey.jpg';
import { ReviewStep } from '@shared/domain/review';
import { useReviewContext } from '../context/ReviewContext';

const steps = [
  { label: 'Basic Info', id: ReviewStep.BASIC_REVIEW },
  { label: 'Entry Strategy', id: ReviewStep.ENTRY_STRATEGY },
  { label: 'Purchase Details', id: ReviewStep.PURCHASE_DETAILS },
  { label: 'Evaluation', id: ReviewStep.EVALUATION },
  { label: 'Legal Review', id: ReviewStep.LEGAL },
  { label: 'Negotiation and Procurement', id: ReviewStep.NEGOTIATION },
];

interface ReviewStepperProps {
  handleBack: (step: ReviewStep) => void;
  rfp: boolean;
  loading?: boolean;
}

const ColorlibConnector = MuiStyled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: { top: 24 },
  [`.${stepConnectorClasses.line}`]: {
    'height': 4,
    'border': 0,
    'backgroundColor':
      theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#e5e7eb',
    'borderRadius': 2,
    'position': 'relative',
    'overflow': 'hidden',
    'transition': 'background-color .2s ease',
    '&::after': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 0,
      height: '100%',
      width: 0,
      backgroundImage:
        'linear-gradient(95deg, rgb(43,144,227) 0%, rgb(43,144,227) 100%)',
      transition: 'width .35s ease',
    },
  },
  [`&.${stepConnectorClasses.active} .${stepConnectorClasses.line}::after`]: {
    width: '100%',
  },
  [`&.${stepConnectorClasses.completed} .${stepConnectorClasses.line}::after`]:
    {
      width: '100%',
    },
}));

const ColorlibStepIconRoot = MuiStyled(motion.div)<{
  ownerState: { completed?: boolean; active?: boolean };
}>(({ theme, ownerState }) => ({
  position: 'relative',
  backgroundColor:
    ownerState.active || ownerState.completed
      ? 'rgb(43,144,227)'
      : theme.palette.mode === 'dark'
        ? theme.palette.grey[700]
        : '#cbd5e1',
  zIndex: 1,
  color: '#fff',
  width: 48,
  height: 48,
  display: 'flex',
  borderRadius: '50%',
  justifyContent: 'center',
  alignItems: 'center',
  boxShadow:
    ownerState.active || ownerState.completed
      ? '0 0 0 3px rgba(43,144,227,0.25)'
      : 'none',
  cursor: 'pointer',
}));

function ColorlibStepIcon(props: StepIconProps) {
  const { active, completed, className } = props;
  return (
    <ColorlibStepIconRoot
      ownerState={{ completed, active }}
      className={className}
      initial={{ scale: 0.95, opacity: 0.9 }}
      animate={{ scale: active ? 1.05 : 1, opacity: 1 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      aria-hidden
    >
      {completed || active
        ? (
            <Box
              component="img"
              src={kandirLogoLink}
              alt="Step active"
              sx={{ width: '100%', height: '100%' }}
            />
          )
        : (
            <Box
              component="img"
              src={kandirLogoGreyLink}
              alt="Step pending"
              sx={{ width: '100%', height: '100%' }}
            />
          )}
    </ColorlibStepIconRoot>
  );
}

function ReviewStepper(props: ReviewStepperProps) {
  const { handleBack, rfp, loading = false } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { reviewStep, setReviewStep, setCurrentReview, lastStepSaved } = useReviewContext();
  const customSteps = useMemo(() => {
    return rfp ? [...steps, { label: 'RFPs', id: ReviewStep.RFP }] : steps;
  }, [rfp]);

  const [mobileIndex, setMobileIndex] = useState(0);

  useEffect(() => {
    const safeIndex = Math.min(reviewStep, customSteps.length - 1);
    setMobileIndex(safeIndex);
  }, [reviewStep, customSteps.length]);

  return (
    <Box
      className="review-stepper-container"
      sx={{
        marginTop: 'var(--spacing-md)',
        padding: '16px 20px',
        borderRadius: '12px',
        background: '#fafafa',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
        width: '100%',
        marginLeft: 'auto',
        marginRight: 'auto',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 6px 18px rgba(0, 0, 0, 0.08)',
        },
      }}
    >
      {isMobile
        ? (
            <Box
              sx={{
                position: 'sticky',
                bottom: 0,
                zIndex: 5,
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(8px)',
                borderTop: '1px solid #e5e7eb',
                boxShadow: '0 -6px 18px rgba(16, 24, 40, 0.08)',
                borderRadius: '12px 12px 0 0',
                padding: '8px 12px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '4px',
                }}
              >
                <Box
                  component="span"
                  sx={{
                    fontWeight: 600,
                    fontSize: '15px',
                    color: '#2a2018',
                    whiteSpace: 'normal',
                  }}
                >
                  {customSteps[mobileIndex]?.label || ''}
                </Box>
                <span style={{ fontSize: 12, color: '#8a7d6e' }}>
                  {mobileIndex + 1}
                  {' '}
                  /
                  {customSteps.length}
                </span>
              </Box>
              <MobileStepper
                variant="dots"
                steps={customSteps.length}
                position="static"
                activeStep={mobileIndex}
                nextButton={(
                  <Button
                    size="small"
                    onClick={() => {
                      const maxAllowed = Math.min(
                        lastStepSaved || 0,
                        customSteps.length - 1,
                      );

                      if (mobileIndex < maxAllowed && !loading) {
                        const next = mobileIndex + 1;
                        setMobileIndex(next);
                        setCurrentReview(null);
                        setReviewStep(next);
                        handleBack(customSteps[next].id);
                      }
                    }}
                    disabled={
                      loading || mobileIndex >= Math.min(lastStepSaved || 0, customSteps.length - 1)
                    }
                  >
                    Next
                    {theme.direction === 'rtl'
                      ? (
                          <KeyboardArrowLeft />
                        )
                      : (
                          <KeyboardArrowRight />
                        )}
                  </Button>
                )}
                backButton={(
                  <Button
                    size="small"
                    onClick={() => {
                      if (mobileIndex > 0 && !loading) {
                        const prev = mobileIndex - 1;
                        setMobileIndex(prev);
                        setCurrentReview(null);
                        setReviewStep(prev);
                        handleBack(customSteps[prev].id);
                      }
                    }}
                    disabled={loading || mobileIndex === 0}
                  >
                    {theme.direction === 'rtl'
                      ? (
                          <KeyboardArrowRight />
                        )
                      : (
                          <KeyboardArrowLeft />
                        )}
                    Back
                  </Button>
                )}
              />
            </Box>
          )
        : (
            <Stepper
              alternativeLabel
              activeStep={reviewStep}
              connector={<ColorlibConnector />}
              className="review-stepper"
            >
              {customSteps.map((stepItem, i) => (
                <Step
                  key={stepItem.id}
                  onClick={() => {
                    if (i <= (lastStepSaved || 0) && !loading) {
                      setCurrentReview(null);
                      setReviewStep(i);
                      handleBack(stepItem.id);
                    }
                  }}
                  completed={i < reviewStep}
                  disabled={i > (lastStepSaved || 0) || loading}
                  title={loading ? 'Loading...' : i <= (lastStepSaved || 0) ? 'Go to this step' : 'Step locked'}
                  tabIndex={i <= (lastStepSaved || 0) && !loading ? 0 : -1}
                  onKeyDown={(e) => {
                    if (i <= (lastStepSaved || 0) && !loading && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      setCurrentReview(null);
                      setReviewStep(i);
                      handleBack(stepItem.id);
                    }
                  }}
                  sx={{
                    cursor: loading ? 'wait' : i <= reviewStep ? 'pointer' : 'not-allowed',
                    opacity: loading ? 0.6 : 1,
                  }}
                >
                  <StepLabel StepIconComponent={ColorlibStepIcon}>
                    <Box
                      component="span"
                      sx={{
                        fontWeight: 600,
                        fontSize: '14px',
                        color: '#2a2018',
                        whiteSpace: 'normal',
                      }}
                    >
                      {stepItem.label}
                    </Box>
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          )}
    </Box>
  );
}
export default ReviewStepper;
